import { t } from "@lingui/macro";
import { cn } from "@reactive-resume/utils";
import { useCallback, useEffect, useState } from "react";

import { aiService } from "@/client/services/aiService";
import { useAudioChatStore } from "@/client/stores/audioChatStore";

import { recordLegacyMessage, recordLegacyMessages } from "./_utils/legacyTranscriptBridge";
import { TypingText } from "./typingText";

type Message = {
  type: "user" | "ai";
  content: string;
  showIllustration?: string;
};
type messageType = Message[];
type JdChatProps = {
  focusTextarea: () => void;
};

export const JdChat = ({ focusTextarea }: JdChatProps) => {
  const [showJd, setShowJd] = useState(false);
  const [messages, setMessages] = useState<messageType>([]);
  const step = useAudioChatStore((state) => state.step);
  const setStep = useAudioChatStore((state) => state.setStep);
  const chatTextData = useAudioChatStore((state) => state.chatTextData);
  const setIsLoading = useAudioChatStore((state) => state.setIsLoading);
  const updateJobDescription = useAudioChatStore((state) => state.updateJobDescription);
  const updateJobAnalysis = useAudioChatStore((state) => state.updateJobAnalysis);
  const setChatTextData = useAudioChatStore((state) => state.setChatTextData);
  //---

  const pushMessages = useCallback((mes: Message) => {
    setMessages((prevMessages) => [...prevMessages, mes]);
    recordLegacyMessage(mes);
  }, []);

  const handleJobDescription = async (input: string) => {
    updateJobDescription(input);
    setIsLoading(true);
    try {
      const analysis = await aiService.analyzeJobDescription(input);
      updateJobAnalysis(analysis);
      pushMessages({
        type: "ai",
        content:
          "Great! This will help us tailor your resume to match the job. Now, let's move on to your personal details.",
        showIllustration: "personal",
      });
      // navigate to next session
      setTimeout(() => setStep("PER_INFO"), 1500);
    } catch (error) {
      // TODO: handle error
      setStep("PER_INFO");
    } finally {
      setIsLoading(false);
    }
  };
  //---

  useEffect(() => {
    if (step === "JD") {
      const initialMessages: messageType = [
        {
          type: "ai",
          content: t`Hello! I'm here to help you create your resume. Let's start with the job you're targeting. Could you please copy paste the job description of the job posting ?`,
          showIllustration: "jd",
        },
      ];
      setShowJd(true);
      setMessages(initialMessages);
      recordLegacyMessages(initialMessages);
      focusTextarea();
    }
  }, [step]);

  useEffect(() => {
    if (step === "JD" && chatTextData) {
      pushMessages({
        type: "user",
        content: t`${chatTextData}`,
      });
      handleJobDescription(chatTextData);
      setChatTextData("");
    }
  }, [chatTextData]);
  //---

  return (
    <>
      {showJd &&
        messages.map((message, index) => (
          <div key={`jd-${index}`}>
            <div
              className={cn(
                "mx-1 mb-3 max-w-[80%] rounded-lg px-4 py-2 text-base shadow-md",
                message.type === "user"
                  ? "ml-auto bg-blue-100 text-blue-800"
                  : "bg-gray-100 text-gray-800",
              )}
            >
              {message.type === "ai" ? (
                <div className="flex items-center gap-2">
                  <div className="grow">
                    <TypingText message={message.content} />
                  </div>

                  {message.showIllustration === "jd" && (
                    <div className="h-32 w-32 shrink-0">
                      <img
                        alt="image"
                        className="float-right h-full scale-125 object-cover object-center"
                        src="/backgrounds/copyDocumentChat.svg"
                      />
                    </div>
                  )}

                  {message.showIllustration === "personal" && (
                    <div className="h-32 w-32 shrink-0">
                      <img
                        alt="image"
                        className="float-right h-full scale-125 object-cover object-center"
                        src="/backgrounds/personalInfoChat.svg"
                      />
                    </div>
                  )}
                </div>
              ) : (
                message.content
              )}
            </div>
          </div>
        ))}
    </>
  );
};
